import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ServiceConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function ServiceConfigEditor({ flow, canEdit, onSave }: ServiceConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Edit state
  const [editedType, setEditedType] = useState('')
  const [editedPort, setEditedPort] = useState('')
  const [editedTargetPort, setEditedTargetPort] = useState('')

  const handleEdit = () => {
    setEditedType(flow.spec?.service?.type || 'ClusterIP')
    setEditedPort(flow.spec?.service?.port?.toString() || '1880')
    setEditedTargetPort(flow.spec?.service?.targetPort?.toString() || '1880')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        service: {
          type: editedType.trim() || 'ClusterIP',
          port: parseInt(editedPort) || 1880,
          targetPort: parseInt(editedTargetPort) || 1880,
        }
      }
    }

    onSave(updatedFlow, t('detail.serviceConfig'))
    setIsEditing(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.serviceConfig', 'Service Configuration')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Type */}
          <div>
            <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.serviceType', 'Type')}
            </label>
            <select
              id="serviceType"
              value={editedType}
              onChange={(e) => setEditedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              <option value="ClusterIP">ClusterIP</option>
              <option value="NodePort">NodePort</option>
              <option value="LoadBalancer">LoadBalancer</option>
            </select>
          </div>

          {/* Port */}
          <div>
            <label htmlFor="servicePort" className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.servicePort', 'Port')}
            </label>
            <input
              id="servicePort"
              type="number"
              value={editedPort}
              onChange={(e) => setEditedPort(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder="1880"
            />
          </div>

          {/* Target Port */}
          <div>
            <label htmlFor="targetPort" className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.serviceTargetPort', 'Target Port')}
            </label>
            <input
              id="targetPort"
              type="number"
              value={editedTargetPort}
              onChange={(e) => setEditedTargetPort(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder="1880"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.serviceType', 'Type')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.service?.type || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.servicePort', 'Port')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.service?.port || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.serviceTargetPort', 'Target Port')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.service?.targetPort || '-'}</dd>
          </div>
        </dl>
      )}
    </div>
  )
}
